import type { ReactElement } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useParams,
  useRouteError,
} from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { DashboardPlaceholder } from '@/features/dashboard/DashboardPlaceholder';
import { AuthGuard, GuestGuard, TenantGuard } from '@/router-guards';

// Sprint 4 (C1) — DashboardPage is the lazy chunk used by the root
// index route. The legacy `DashboardPlaceholder` import is preserved
// so the catch-all `*` routes below still compile; the placeholder
// remains as a fallback for unmatched paths.
void DashboardPlaceholder;

/**
 * Route-level fallback for render/loader errors inside the protected
 * tree. Without it, react-router's default error page replaced the whole
 * app — the client hit it editing a legacy client whose
 * `identificationType` broke the form's Zod parse. A friendly screen
 * (with the message for support) is strictly better than a blank page.
 */
function RouteError(): ReactElement {
  const error = useRouteError();
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Error inesperado';
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-lg font-semibold">Algo salió mal</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        No pudimos mostrar esta pantalla. Recargá la página; si el problema persiste, pasale este
        detalle al equipo.
      </p>
      <code className="max-w-md overflow-auto rounded bg-muted px-3 py-2 text-xs">{message}</code>
      <button
        type="button"
        className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
        onClick={() => window.location.reload()}
      >
        Recargar
      </button>
    </div>
  );
}

/**
 * Router — composition root for React Router v7.
 *
 * createBrowserRouter so we can:
 *   - colocate `<Outlet />` with each route's wrapper via the
 *     `element` shorthand (no nested `<Route element={...}>` trees);
 *   - lazily code-split feature pages in Slice 1 via `lazy:`.
 *
 * Two top-level layouts:
 *   - PUBLIC:   /login, /forgot-password, /reset-password
 *   - PROTECTED: anything else — wrapped in AppShell + 3 guards.
 *
 * Permission gating:
 *   Inside the app shell, every protected page mounts its own
 *   `<PermissionGate>` so deep-links stay navigable: a route like
 *   `/users` resolves to the UsersPage, which renders a friendly
 *   "you don't have access" fallback when the user lacks
 *   `users.view`. The login redirect on 401 still funnels through
 *   `AuthGuard`.
 *
 *   Slice 1 PR#6 added the CRM (`/crm`, `/crm/clients`, `/crm/providers`),
 *   users (`/users`, ADMIN-only rendered fallback inside), and
 *   geography (`/geo`, open to all authenticated users — read-only).
 *
 * The 401 fallback (catch-all → /login) lives at the end so unknown
 * paths in the protected tree land gracefully.
 */
export function AppRouter(): ReactElement {
  const router = createBrowserRouter([
    {
      path: '/login',
      element: (
        <GuestGuard>
          <LoginPage />
        </GuestGuard>
      ),
    },
    {
      path: '/forgot-password',
      element: (
        <GuestGuard>
          <ForgotPasswordPage />
        </GuestGuard>
      ),
    },
    {
      path: '/reset-password',
      element: (
        <GuestGuard>
          <ResetPasswordPage />
        </GuestGuard>
      ),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <TenantGuard>
            <AppShell />
          </TenantGuard>
        </AuthGuard>
      ),
      errorElement: <RouteError />,
      children: [
        {
          index: true,
          lazy: async () => {
            const mod = await import('@/features/dashboard/DashboardPage');
            return { Component: mod.DashboardPage };
          },
        },
        // ----- Slice 1 PR#5a/b — catalog -----
        {
          path: 'catalog',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/CatalogIndexPage');
            return { Component: mod.CatalogIndexPage };
          },
        },
        {
          // PermissionGate for defaultCost lives inside ProductsPage.
          path: 'catalog/products',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/ProductsPage');
            return { Component: mod.ProductsPage };
          },
        },
        {
          path: 'catalog/product-models',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/ProductModelsPage');
            return { Component: mod.ProductModelsPage };
          },
        },
        {
          path: 'catalog/product-types',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/ProductTypesPage');
            return { Component: mod.ProductTypesPage };
          },
        },
        {
          path: 'catalog/brands',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/BrandsPage');
            return { Component: mod.BrandsPage };
          },
        },
        {
          // ADMIN-only — page-level PermissionGate renders the
          // "you don't have access" fallback.
          path: 'currencies',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/CurrenciesPage');
            return { Component: mod.CurrenciesPage };
          },
        },
        {
          // ADMIN-only — same gate pattern.
          path: 'exchange-rates',
          lazy: async () => {
            const mod = await import('@/features/catalog/pages/ExchangeRatesPage');
            return { Component: mod.ExchangeRatesPage };
          },
        },
        // ----- Slice 1 PR#6 — CRM, users, geography -----
        {
          path: 'crm',
          lazy: async () => {
            const mod = await import('@/features/crm/pages/CrmIndexPage');
            return { Component: mod.CrmIndexPage };
          },
        },
        {
          path: 'crm/clients',
          lazy: async () => {
            const mod = await import('@/features/crm/pages/ClientsPage');
            return { Component: mod.ClientsPage };
          },
        },
        {
          path: 'crm/providers',
          lazy: async () => {
            const mod = await import('@/features/crm/pages/ProvidersPage');
            return { Component: mod.ProvidersPage };
          },
        },
        // ----- Sprint 2 A3 — CRM Analytics -----
        {
          path: 'crm/analytics',
          lazy: async () => {
            const mod = await import('@/features/crm/pages/CrmAnalyticsPage');
            return { Component: mod.CrmAnalyticsPage };
          },
        },
        {
          // Slice 2 — seller follow-up queue. Page-level gate renders
          // the no-access fallback for roles without `crm.manage_tasks`.
          path: 'crm/tasks',
          lazy: async () => {
            const mod = await import('@/features/crm/pages/SellerTasksPage');
            return { Component: mod.SellerTasksPage };
          },
        },
        {
          // PR#6 — page-level ADMIN gate renders the no-access fallback.
          path: 'users',
          lazy: async () => {
            const mod = await import('@/features/users/pages/UsersPage');
            return { Component: mod.UsersPage };
          },
        },
        {
          // HR expediente — list + detail. Page-level gate renders the
          // no-access fallback for roles without `hr.view_workers`.
          path: 'workers',
          lazy: async () => {
            const mod = await import('@/features/workers/pages/WorkersPage');
            return { Component: mod.WorkersPage };
          },
        },
        {
          path: 'workers/:id',
          lazy: async () => {
            const mod = await import('@/features/workers/pages/WorkerDetailPage');
            return { Component: mod.WorkerDetailPage };
          },
        },
        {
          // PR#6 — open to all authenticated users.
          path: 'geo',
          lazy: async () => {
            const mod = await import('@/features/geo/pages/GeographyPage');
            return { Component: mod.GeographyPage };
          },
        },
        // ----- Slice 2 PR#3 — Inventory + Sales + PDF -----
        {
          path: 'inventory',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/InventoryIndexPage');
            return { Component: mod.InventoryIndexPage };
          },
        },
        {
          path: 'inventory/warehouses',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/WarehousesPage');
            return { Component: mod.WarehousesPage };
          },
        },
        {
          path: 'inventory/items',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/InventoryItemsPage');
            return { Component: mod.InventoryItemsPage };
          },
        },
        {
          path: 'inventory/receipts',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/WarehouseReceiptsPage');
            return { Component: mod.WarehouseReceiptsPage };
          },
        },
        {
          path: 'inventory/movements',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/InventoryMovementsPage');
            return { Component: mod.InventoryMovementsPage };
          },
        },
        // ----- Sprint 2 A4 — Inventory Valuation analytics -----
        // Permission-gated by `inventory.view_costs` (ADMIN + SELLER).
        // The page renders a fallback for roles without the permission.
        {
          path: 'inventory/valuation',
          lazy: async () => {
            const mod = await import('@/features/inventory/pages/ValuationPage');
            return { Component: mod.ValuationPage };
          },
        },
        {
          path: 'sales',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/SalesIndexPage');
            return { Component: mod.SalesIndexPage };
          },
        },
        {
          path: 'quotations',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/QuotationsIndexPage');
            return { Component: mod.QuotationsIndexPage };
          },
        },
        {
          path: 'quotations/:id',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/QuotationDetailPage');
            return { Component: mod.QuotationDetailPage };
          },
        },
        {
          path: 'sales-orders',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/SalesOrdersIndexPage');
            return { Component: mod.SalesOrdersIndexPage };
          },
        },
        {
          path: 'sales-orders/:id',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/SalesOrderDetailPage');
            return { Component: mod.SalesOrderDetailPage };
          },
        },
        {
          path: 'commission-rules',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/CommissionRulesPage');
            return { Component: mod.CommissionRulesPage };
          },
        },
        {
          path: 'installment-plans',
          lazy: async () => {
            const mod = await import('@/features/sales/pages/InstallmentPlansPage');
            return { Component: mod.InstallmentPlansPage };
          },
        },
        // ----- Slice 3 PR#4 — Finance -----
        {
          path: 'finance',
          lazy: async () => {
            const mod = await import('@/features/finance/pages/FinanceIndexPage');
            return { Component: mod.FinanceIndexPage };
          },
          children: [
            { index: true, element: <FinanceIndexRedirect /> },
            {
              path: 'receivables',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/ReceivablesPage');
                return { Component: mod.ReceivablesPage };
              },
            },
            {
              path: 'receivables/:id',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/ReceivableDetailPage');
                return { Component: mod.ReceivableDetailPage };
              },
            },
            {
              path: 'receivables/:id/collect',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/CollectPaymentPage');
                return { Component: mod.CollectPaymentPage };
              },
            },
            {
              path: 'payables',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/PayablesPage');
                return { Component: mod.PayablesPage };
              },
            },
            {
              path: 'accounts',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/BankAccountsPage');
                return { Component: mod.BankAccountsPage };
              },
            },
            {
              path: 'expenses',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/RecurringExpensesPage');
                return { Component: mod.RecurringExpensesPage };
              },
            },
            {
              // Sub-PR#2.5 — operational expense report (ADMIN-only
              // at the tab level; route stays mounted so a deep-link
              // is still navigable). The page is read-only.
              path: 'operational',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/ExpensesPage');
                return { Component: mod.ExpensesPage };
              },
            },
            // ----- sdd/finanzas-modulo PR#3 T3.13 — new pages -----
            // MovementsPage is open to any user with VIEW_REPORTS
            // (ADMIN via ALL_PERMISSIONS); SELLER + WAREHOUSE lack
            // the perm and the page renders its own no-access
            // fallback. Route stays mounted so deep-links resolve.
            {
              path: 'movements',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/MovementsPage');
                return { Component: mod.MovementsPage };
              },
            },
            // GastosPage is the PENDING_APPROVAL inbox. The page is
            // open to any authenticated user (the list shows
            // everyone's pending requests); the Aprobar/Rechazar
            // buttons gate themselves by permission.
            {
              path: 'gastos',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/GastosPage');
                return { Component: mod.GastosPage };
              },
            },
            // ConfiguracionPage is ADMIN-only at the FinanceIndex tab
            // level (T3.14 hides the tab for SELLER); the route
            // stays mounted so the deep-link still resolves.
            {
              path: 'configuracion',
              lazy: async () => {
                const mod = await import('@/features/finance/pages/ConfiguracionPage');
                return { Component: mod.ConfiguracionPage };
              },
            },
          ],
        },
        {
          path: 'finance/reports',
          lazy: async () => {
            const mod = await import('@/features/finance/pages/ReportsPage');
            return { Component: mod.ReportsPage };
          },
        },
        {
          path: 'finance/seller-stats',
          lazy: async () => {
            const mod = await import('@/features/finance/pages/SellerStatsPage');
            return { Component: mod.SellerStatsPage };
          },
        },
        {
          path: 'finance/equity',
          lazy: async () => {
            const mod = await import('@/features/finance/pages/EquityPage');
            return { Component: mod.EquityPage };
          },
        },
        // ----- Slice 4 PR#4 — Dispatch + Notifications -----
        {
          path: 'warehouse',
          lazy: async () => {
            const mod = await import('@/features/dispatch/pages/WarehouseInboxPage');
            return { Component: mod.WarehouseInboxPage };
          },
        },
        {
          path: 'dispatches',
          lazy: async () => {
            const mod = await import('@/features/dispatch/pages/DispatchIndexPage');
            return { Component: mod.DispatchIndexPage };
          },
        },
        {
          path: 'dispatches/:id',
          lazy: async () => {
            const mod = await import('@/features/dispatch/pages/DispatchDetailPage');
            return { Component: mod.DispatchDetailPage };
          },
        },
        // ----- Sprint 2 A1 — Dispatch KPIs analytics -----
        {
          path: 'dispatches/analytics',
          lazy: async () => {
            const mod = await import('@/features/dispatch/pages/DispatchAnalyticsPage');
            return { Component: mod.DispatchAnalyticsPage };
          },
        },
        {
          path: 'notifications',
          lazy: async () => {
            const mod = await import('@/features/notifications/pages/NotificationCenterPage');
            return { Component: mod.NotificationCenterPage };
          },
        },
        // ----- Slice 5 PR#3 — RF-13 International Purchases (FE) -----
        {
          // PR#3 — index page (list + filters + create drawer).
          // Page-level PermissionGate gates `purchases.create` for
          // the drawer; the list itself is open to anyone with
          // `purchases.view`.
          path: 'purchases',
          lazy: async () => {
            const mod = await import('@/features/purchases/pages/PurchaseOrdersIndexPage');
            return { Component: mod.PurchaseOrdersIndexPage };
          },
        },
        {
          // PR#3 — dedicated create route (deep-link friendly).
          path: 'purchases/new',
          lazy: async () => {
            const mod = await import('@/features/purchases/pages/PurchaseOrderNewPage');
            return { Component: mod.PurchaseOrderNewPage };
          },
        },
        {
          // PR#3 — detail page (header + items + timeline + movements).
          path: 'purchases/:id',
          lazy: async () => {
            const mod = await import('@/features/purchases/pages/PurchaseOrderDetailPage');
            return { Component: mod.PurchaseOrderDetailPage };
          },
        },
        {
          // PR#3 — dedicated receive page (WAREHOUSE only). The
          // page itself renders the PermissionGate fallback when
          // the user lacks `purchases.receive`.
          path: 'purchases/:id/receive',
          lazy: async () => {
            const mod = await import('@/features/purchases/pages/PurchaseOrderReceivePage');
            return { Component: mod.PurchaseOrderReceivePage };
          },
        },
        // ----- Sprint 2 A2 — Purchases KPIs analytics -----
        {
          path: 'purchases/analytics',
          lazy: async () => {
            const mod = await import('@/features/purchases/pages/PurchaseAnalyticsPage');
            return { Component: mod.PurchaseAnalyticsPage };
          },
        },
        {
          // Sprint 1 B5 — shipments moved under `/purchases`.
          // The legacy `/shipments` path (still rendered as a
          // Navigate below) is kept only for backward-compat with
          // existing bookmarks; new code MUST link to
          // `/purchases/shipments`.
          path: 'purchases/shipments',
          lazy: async () => {
            const mod = await import('@/features/transits/pages/ShipmentsIndexPage');
            return { Component: mod.ShipmentsIndexPage };
          },
        },
        {
          path: 'purchases/shipments/:id',
          lazy: async () => {
            const mod = await import('@/features/transits/pages/ShipmentDetailPage');
            return { Component: mod.ShipmentDetailPage };
          },
        },
        {
          // Backward-compat redirects for legacy URLs — Sprint 1 B3.
          // `replace` so the old URL does not pollute browser
          // history (matches the navigation a `<Link>` would
          // produce). A `<Navigate>` inside an authenticated route
          // also re-runs the auth guard on the destination.
          path: 'shipments',
          element: <Navigate to="/purchases/shipments" replace />,
        },
        {
          path: 'shipments/:id',
          // `<Navigate to=...>` does NOT auto-substitute the `:id`
          // param, so the dest is computed from the matched params
          // via a tiny wrapper component.
          element: <ShipmentRedirect />,
        },
        // Backward-compat redirects for repointed sidebar items (B4).
        // `/reports` and `/settings` were dead endpoints in Slice 0;
        // the sidebar now points at `/finance/reports` and
        // `/settings/notifications` so any bookmark to the legacy
        // paths still reaches a real page.
        {
          path: 'reports',
          element: <Navigate to="/finance/reports" replace />,
        },
        {
          path: 'settings',
          element: <Navigate to="/settings/notifications" replace />,
        },
        {
          path: 'settings/notifications',
          lazy: async () => {
            const mod = await import('@/features/notifications/pages/NotificationPreferencesPage');
            return { Component: mod.NotificationPreferencesPage };
          },
        },
        // ----- Slice 8 Sub-PR#2c — Authorizations (Phase 4) -----
        {
          // Sub-PR#2c — index page (inbox + sent tabs + status filter +
          // admin "see all"). PermissionGate INSIDE the page (page-level)
          // also gates per-row action buttons. The route is lazy-loaded
          // so the authorizations chunk ships only when needed.
          path: 'authorizations',
          lazy: async () => {
            const mod = await import('@/features/authorizations/pages/AuthorizationsIndexPage');
            return { Component: mod.AuthorizationsIndexPage };
          },
        },
        {
          // Sub-PR#2c — ADMIN-only template catalog. The page renders
          // a no-access fallback when the user lacks the create perm.
          path: 'authorizations/templates',
          lazy: async () => {
            const mod = await import('@/features/authorizations/pages/AuthorizationTemplatesPage');
            return { Component: mod.AuthorizationTemplatesPage };
          },
        },
        {
          // Sub-PR#2c — deep-link to a single request detail with the
          // respond + cancel action footer.
          path: 'authorizations/:id',
          lazy: async () => {
            const mod = await import('@/features/authorizations/pages/AuthorizationDetailPage');
            return { Component: mod.AuthorizationDetailPage };
          },
        },
        // ----- Sprint 3 PR#3a — Sistema group: roles catalogue -----
        {
          // Page-level PermissionGate renders the friendly fallback
          // for non-admins (SELLER / WAREHOUSE lack `users.manage_roles`,
          // so they see the "no-access" variant rather than a 403 wall).
          // The route is lazy-loaded so the roles chunk only ships
          // when ADMIN opens the page.
          path: 'roles',
          lazy: async () => {
            const mod = await import('@/features/roles/pages/RolesPage');
            return { Component: mod.RolesPage };
          },
        },
        {
          // ----- Sprint 3 PR#3b — Sistema group: audit log -----
          // Page is a placeholder in PR#3a (this PR); PR#3b replaces
          // the body with the real AuditLogPage that wires the
          // `useAuditLogs` query + the side-drawer. The lazy import
          // path is fixed in this PR so PR#3b MUST NOT rename the
          // file or move the directory.
          path: 'audit',
          lazy: async () => {
            const mod = await import('@/features/audit/pages/AuditLogPage');
            return { Component: mod.default };
          },
        },
        {
          path: '*',
          lazy: async () => {
            const mod = await import('@/features/dashboard/DashboardPage');
            return { Component: mod.DashboardPage };
          },
        },
      ],
    },
    {
      path: '*',
      element: (
        <AuthGuard>
          <TenantGuard>
            <AppShell />
          </TenantGuard>
        </AuthGuard>
      ),
      children: [{ index: true, element: <DashboardPlaceholder /> }],
    },
  ]);

  return <RouterProvider router={router} />;
}

/**
 * When /finance mounts with no child selected, redirect to the
 * receivables tab. FinanceIndexPage renders the tab nav, so the
 * child Outlet always needs an active child.
 */
function FinanceIndexRedirect(): ReactElement {
  return <Navigate to="/finance/accounts" replace />;
}

/**
 * Sprint 1 B3 — backward-compat redirect from `/shipments/:id` to
 * the new `/purchases/shipments/:id`. React Router's `<Navigate>`
 * string form does not interpolate path params, so we read them
 * with `useParams` and compose the destination by hand.
 */
function ShipmentRedirect(): ReactElement {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/purchases/shipments/${id ?? ''}`} replace />;
}
